import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ProfilePopup = ({ user, onClose, onLike, onPass, onMessage }) => {
  const navigate = useNavigate(); 
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const userImages = useMemo(() => {
    const urls = [];
    if (Array.isArray(user?.images)) {
      urls.push(...user.images);
    }
    if (Array.isArray(user?.photos)) {
      urls.push(...user.photos.map(p => p?.image_url));
    }
    if (user?.profile_image || user?.image || user?.profile_picture) {
      urls.push(user.profile_image || user.image || user.profile_picture);
    }
    const cleaned = urls.filter(Boolean).map(String);
    const unique = Array.from(new Set(cleaned));

    // final fallback
    if (unique.length === 0) {
      return [{ id: 1, color: 'linear-gradient(135deg, #003A8F, #60a5fa)' }];
    }

    return unique;
  }, [user]);

  // ✅ Reset index when opening a different user
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [user?.id]);

  const userTags = useMemo(() => {
    const tags = user?.tags || user?.interests || [];
    return Array.isArray(tags) && tags.length ? tags : ['Looking for connection'];
  }, [user]);

  const userName = user?.name || user?.full_name || 'User';
  const userAge = user?.age || '';
  const userJob = user?.occupation || user?.job || 'Not specified';
  const userDistance = user?.distance ? `${Math.round(user.distance)} km away` : 'Nearby';
  const userBio = user?.bio || 'No bio available';

  const nextImage = (e) => {
    e?.stopPropagation();
    if (userImages.length <= 1) return;
    setCurrentImageIndex(i => (i + 1) % userImages.length);
  };

  const prevImage = (e) => {
    e?.stopPropagation();
    if (userImages.length <= 1) return;
    setCurrentImageIndex(i => (i - 1 + userImages.length) % userImages.length);
  };

  const handleMessageClick = async () => {
    try {
      // ✅ let Dashboard handle token + API call
      onClose?.();
      if (onMessage) {
        await onMessage(user);
      } else {
        // fallback: navigate somewhere if you want
        console.warn("onMessage prop not provided to ProfilePopup");
      }
    } catch (e) {
      console.error("Failed to open chat", e);
    }
  };

  const handleInfoClick = () => {
    alert(
      `More info about ${userName}:\n\nAge: ${userAge}\nOccupation: ${userJob}\nBio: ${userBio}\nDistance: ${userDistance}`
    );
  };

  const getImageStyle = (image) => {
    if (typeof image === 'object' && image?.color) return { background: image.color };
    if (typeof image === 'string' && image.startsWith('linear-gradient')) return { background: image };

    return {
      backgroundImage: `url(${image})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  };

  const activeImage = userImages[currentImageIndex] ?? userImages[0];

  return (
    <div className="profile-popup-overlay" onClick={onClose}>
      <div className="profile-popup" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <button className="close-btn" onClick={onClose}>×</button>
          <h2>{userName}&apos;s Profile</h2>
        </div>

        <div className="profile-images">
          <div className="main-profile-image" style={getImageStyle(activeImage)}>
            <h2>{userName}{userAge && `, ${userAge}`}</h2>

            {userImages.length > 1 && (
              <>
                <button type="button" className="img-nav-btn left" onClick={prevImage}>‹</button>
                <button type="button" className="img-nav-btn right" onClick={nextImage}>›</button>
              </>
            )}

            <div className="image-indicators">
              {userImages.map((_, index) => (
                <span
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="profile-details">
          <div className="profile-meta">
            <span className="job">{userJob}</span>
            <span className="distance">{userDistance}</span>
          </div>

          <div className="tags">
            {userTags.map((tag, index) => (
              <span key={index} className="tag">{tag}</span>
            ))}
          </div>

          <div className="prompt">
            <strong>What matters most to me right now:</strong><br />
            {userBio}
          </div>

          <div className="profile-actions">
            <button className="btn btn-pass" onClick={() => onPass?.()} title="Pass">✕</button>
            <button className="btn btn-message" onClick={handleMessageClick} title={`Message ${userName}`}>💬</button>
            <button className="btn btn-like" onClick={() => onLike?.()} title="Like">❤</button>
          </div>

          <div className="additional-actions">
            <button className="info-btn" onClick={handleInfoClick} title="More info">
              <i className="fas fa-info-circle"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePopup;
